import { MAX_ENTITIES } from "./constants";
import type { Order } from "./commands";
import type { UnitDef } from "../data/types";
import { UNIT_DEFS } from "../data";
import { GameMap } from "./gamemap";

// Micro-action a unit is currently performing (drives FX + render; derived from
// orders + combat each tick).
export const ACT_IDLE = 0;
export const ACT_MOVE = 1;
export const ACT_FIRE = 2;
export const ACT_BUILD = 3;
export const ACT_RECLAIM = 4;
export const ACT_REPAIR = 5; // restoring a completed-but-damaged friendly's health

// Entity flag bits.
export const F_UNDER_CONSTRUCTION = 1;
export const F_CONVERTER_ON = 2;

/** Per-player streaming economy state (GDD §3). */
export interface EcoState {
  metal: number;
  metalCap: number;
  energy: number;
  energyCap: number;
  metalIncome: number; // M/s (display, last tick)
  metalExpense: number; // M/s (display, last tick)
  energyIncome: number; // E/s
  energyExpense: number; // E/s
  metalWasted: number; // M/s overflowed last tick
  energyWasted: number; // E/s overflowed last tick
  stalled: boolean; // could not fully fund construction last tick
}

function makeEco(): EcoState {
  return {
    metal: 0, metalCap: 0, energy: 0, energyCap: 0,
    metalIncome: 0, metalExpense: 0, energyIncome: 0, energyExpense: 0,
    metalWasted: 0, energyWasted: 0, stalled: false,
  };
}

/**
 * Struct-of-arrays entity store. Hot per-entity data lives in typed arrays;
 * cold data (order queues, factory queues) lives in parallel JS structures.
 */
export class World {
  readonly cap = MAX_ENTITIES;

  // --- hot arrays ---
  alive = new Uint8Array(MAX_ENTITIES);
  kind = new Uint8Array(MAX_ENTITIES);
  owner = new Uint8Array(MAX_ENTITIES);
  defIdx = new Uint16Array(MAX_ENTITIES);
  posX = new Float32Array(MAX_ENTITIES);
  posY = new Float32Array(MAX_ENTITIES);
  prevX = new Float32Array(MAX_ENTITIES);
  prevY = new Float32Array(MAX_ENTITIES);
  velX = new Float32Array(MAX_ENTITIES);
  velY = new Float32Array(MAX_ENTITIES);
  heading = new Float32Array(MAX_ENTITIES);
  prevHeading = new Float32Array(MAX_ENTITIES);
  health = new Float32Array(MAX_ENTITIES);
  maxHealth = new Float32Array(MAX_ENTITIES);
  targetEnt = new Int32Array(MAX_ENTITIES);
  reload = new Float32Array(MAX_ENTITIES);
  rescanPhase = new Uint8Array(MAX_ENTITIES);
  action = new Uint8Array(MAX_ENTITIES);
  fireFlash = new Float32Array(MAX_ENTITIES); // seconds of muzzle-flash remaining (render)
  buildProgress = new Float32Array(MAX_ENTITIES); // 0..1 while under construction
  buildTargetEnt = new Int32Array(MAX_ENTITIES); // what this builder is working on
  flags = new Uint8Array(MAX_ENTITIES);
  metalSpotIdx = new Int32Array(MAX_ENTITIES); // extractor -> spot index, else -1
  rallyX = new Float32Array(MAX_ENTITIES);
  rallyY = new Float32Array(MAX_ENTITIES);
  reclaimMetalStore = new Float32Array(MAX_ENTITIES); // metal left in wreckage/feature
  reclaimEnergyStore = new Float32Array(MAX_ENTITIES); // energy left in feature
  // Position at the previous tick's movement step, used to measure how far a unit
  // actually got (jam detection for group arrival). Per-Sim so replay is clean.
  mvPrevX = new Float32Array(MAX_ENTITIES);
  mvPrevY = new Float32Array(MAX_ENTITIES);

  // --- cold parallel data ---
  orders: Order[][] = [];
  factoryQueue: string[][] = [];

  // --- def index table ---
  defList: UnitDef[] = [];
  defIndexById = new Map<string, number>();

  // --- economy ---
  eco: [EcoState, EcoState, EcoState] = [makeEco(), makeEco(), makeEco()]; // [neutral, player, enemy]

  // --- map + metal spots ---
  gmap: GameMap;
  metalSpotOccupant: Int32Array; // per spot -> ent id (-1 free)

  // --- free list ---
  private freeStack: number[] = [];
  private highWater = 0;

  tick = 0;
  windCurrent = 10;

  constructor(gmap: GameMap) {
    this.gmap = gmap;
    // Build the def index table with a stable ordering (JSON key order).
    for (const id of Object.keys(UNIT_DEFS)) {
      this.defIndexById.set(id, this.defList.length);
      this.defList.push(UNIT_DEFS[id]);
    }
    this.metalSpotOccupant = new Int32Array(gmap.def.metalSpots.length).fill(-1);
    for (let i = 0; i < MAX_ENTITIES; i++) {
      this.orders.push([]);
      this.factoryQueue.push([]);
    }
  }

  def(ent: number): UnitDef {
    return this.defList[this.defIdx[ent]];
  }

  defIndex(id: string): number {
    const i = this.defIndexById.get(id);
    if (i === undefined) throw new Error(`Unknown def ${id}`);
    return i;
  }

  private allocate(): number {
    if (this.freeStack.length > 0) return this.freeStack.pop()!;
    if (this.highWater < MAX_ENTITIES) return this.highWater++;
    return -1;
  }

  spawn(
    defId: string,
    owner: number,
    x: number,
    y: number,
    opts?: { underConstruction?: boolean; heading?: number },
  ): number {
    const e = this.allocate();
    if (e < 0) return -1;
    const def = UNIT_DEFS[defId];
    const di = this.defIndex(defId);
    this.alive[e] = 1;
    this.defIdx[e] = di;
    this.owner[e] = owner;
    this.kind[e] =
      def.kind === "building" ? 1 : def.kind === "feature" ? 3 : 0;
    this.posX[e] = x;
    this.posY[e] = y;
    this.prevX[e] = x;
    this.prevY[e] = y;
    this.velX[e] = 0;
    this.velY[e] = 0;
    this.heading[e] = opts?.heading ?? 0;
    this.prevHeading[e] = this.heading[e];
    this.maxHealth[e] = def.maxHealth;
    this.targetEnt[e] = -1;
    this.reload[e] = 0;
    // Stagger target rescans across entities for amortized targeting.
    this.rescanPhase[e] = e & 7;
    this.action[e] = ACT_IDLE;
    this.fireFlash[e] = 0;
    this.buildTargetEnt[e] = -1;
    this.metalSpotIdx[e] = -1;
    this.reclaimMetalStore[e] = 0;
    this.reclaimEnergyStore[e] = 0;
    this.mvPrevX[e] = x;
    this.mvPrevY[e] = y;
    this.rallyX[e] = x;
    this.rallyY[e] = y;
    this.orders[e].length = 0;
    this.factoryQueue[e].length = 0;
    const uc = opts?.underConstruction ?? false;
    this.flags[e] = uc ? F_UNDER_CONSTRUCTION : 0;
    this.buildProgress[e] = uc ? 0 : 1;
    // Under-construction entities start at a small "nanoframe" health and heal
    // to full as construction proceeds.
    this.health[e] = uc ? Math.max(1, def.maxHealth * 0.05) : def.maxHealth;
    return e;
  }

  kill(e: number): void {
    if (!this.alive[e]) return;
    this.alive[e] = 0;
    // Release metal spot if this was an extractor.
    const spot = this.metalSpotIdx[e];
    if (spot >= 0 && this.metalSpotOccupant[spot] === e) {
      this.metalSpotOccupant[spot] = -1;
    }
    this.metalSpotIdx[e] = -1;
    this.orders[e].length = 0;
    this.factoryQueue[e].length = 0;
    this.buildTargetEnt[e] = -1;
    this.freeStack.push(e);
  }

  isAlive(e: number): boolean {
    return e >= 0 && e < MAX_ENTITIES && this.alive[e] === 1;
  }

  underConstruction(e: number): boolean {
    return (this.flags[e] & F_UNDER_CONSTRUCTION) !== 0;
  }

  /** Iterate the currently used index range (may include dead slots). */
  get count(): number {
    return this.highWater;
  }

  liveCountForOwner(owner: number): number {
    let n = 0;
    for (let e = 0; e < this.highWater; e++) {
      if (this.alive[e] && this.owner[e] === owner) n++;
    }
    return n;
  }
}
